import {
  useQueryClient,
  type UseMutationResult,
  useMutation,
} from "@tanstack/react-query"

import { invalidationIntents, queryKeys } from "~/api/cache"

import type { UpdateProposalFinanceDto } from "../dto/proposal.dto"
import { ProposalService } from "../services/proposal.service"

const proposalService = new ProposalService()

function useUpdateProposalFinanceProduct(): UseMutationResult<
  void,
  unknown,
  {
    id: string
    data: UpdateProposalFinanceDto
  },
  { previousData: undefined | void }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: UpdateProposalFinanceDto
    }): Promise<void> => {
      return proposalService.updateProposalFinanceProduct(id, data)
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.proposal.byId(variables.id),
      })

      const previousData = queryClient.getQueryData<void>(
        queryKeys.proposal.byId(variables.id),
      )

      queryClient.setQueryData<void>(
        queryKeys.proposal.byId(variables.id),
        (old) => {
          return old
        },
      )

      return { previousData }
    },
    onError: (_err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData<void>(
          queryKeys.proposal.byId(variables.id),
          context.previousData,
        )
      }
    },
    onSettled: async (_data, _error, variables) => {
      await invalidationIntents.proposalFinanceProductChanged(
        queryClient,
        variables.id,
      )

      try {
        await proposalService.fetchProposalPricing(variables.id)
      } catch (_pricingError) {
        // Keep the product finance update flow non-blocking while pricing settles.
      }

      await new Promise((resolve) => setTimeout(resolve, 1000))

      await queryClient.refetchQueries({
        queryKey: queryKeys.proposal.savings(variables.id),
        type: `active`,
      })
    },
  })
}

export default useUpdateProposalFinanceProduct
